import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Badge,
  Button,
  Form,
  Row,
  Col,
  InputGroup,
  Dropdown,
  Spinner
} from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { useAuth } from "../../contexts/AuthContext";
import { adminService } from "../../services/adminService";
import OfferModal from "./OfferModal";
import toast from "react-hot-toast";

const Offers: React.FC = () => {
  const { hasPermission } = useAuth();

const canReadOffers = hasPermission("offers:read");
const canWriteOffers = hasPermission("offers:write");

  const [offers, setOffers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [selectedOffer, setSelectedOffer] = useState<any>(null);

  useEffect(() => {
    loadOffers();
  }, []);

  const loadOffers = async () => {

    try {

      setLoading(true);

      const response = await adminService.getOffers();

      if (response.success && response.data) {
        setOffers(response.data);
      }

    } catch (error) {

      toast.error("Failed to load offers");
      console.error(error);

    } finally {

      setLoading(false);

    }

  };

  const handleDeleteOffer = async (offer: any) => {

    if (!window.confirm(`Delete offer "${offer.title}"?`)) {
      return;
    }

    try {

      const response = await adminService.deleteOffer(offer.id);

      if (response.success) {
        toast.success("Offer deleted successfully");
        loadOffers();
      }

    } catch (error) {

      toast.error("Failed to delete offer");

    }

  };

  const handleEditOffer = (offer: any) => {

    setModalMode("edit");
    setSelectedOffer(offer);
    setShowModal(true);

  };

  const handleSaveOffer = async (offerData: any) => {

    if (!canWriteOffers) {
      toast.error("You do not have permission to modify offers");
      return;
    }

    try {

      let response;

      if (modalMode === "add") {

        response = await adminService.createOffer(offerData);
        toast.success("Offer created successfully");

      } else {

        response = await adminService.updateOffer(selectedOffer.id, offerData);
        toast.success("Offer updated successfully");

      }

      if (response.success) {

        setShowModal(false);
        loadOffers();

      }

    } catch (error) {

      toast.error("Failed to save offer");

    }

  };

  const filteredOffers = offers.filter((offer) =>
    offer.title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status: boolean) => {

    return status
      ? <Badge bg="success">Active</Badge>
      : <Badge bg="secondary">Inactive</Badge>;

  };

  const getHomeBadge = (show: boolean) => {

    return show
      ? <Badge bg="primary">Yes</Badge>
      : <Badge bg="secondary">No</Badge>;

  };

  const formatDate = (date?: string) => {

    if (!date) return "—";

    const d = new Date(date);

    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });

  };

  if (!canReadOffers) {
    return (
      <div className="container-fluid py-4">
        <div className="alert alert-warning">
          <i className="bi bi-exclamation-triangle me-2"></i>
          You do not have permission to view offers.
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Offers - MyCinemas Admin</title>
      </Helmet>

      <div className="fade-in">

        <div className="d-flex justify-content-between align-items-center mb-4">

          <div>
            <h2 className="mb-1">Offers</h2>
            <p className="text-muted mb-0">Manage promotional offers</p>
          </div>

          {canWriteOffers && (
  <Button
    variant="primary"
    onClick={() => {
      setModalMode("add");
      setSelectedOffer(null);
      setShowModal(true);
    }}
  >
    <i className="bi-plus-circle me-1"></i>
    Add Offer
  </Button>
)}

        </div>

        <Card>

          <Card.Header>

            <Row className="align-items-center">

              <Col>
                <h5 className="mb-0">
                  All Offers ({filteredOffers.length})
                </h5>
              </Col>

              <Col md={3}>

                <InputGroup>

                  <InputGroup.Text>
                    <i className="bi-search"></i>
                  </InputGroup.Text>

                  <Form.Control
                    type="text"
                    placeholder="Search offers..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />

                </InputGroup>

              </Col>

            </Row>

          </Card.Header>

          <Card.Body className="p-0">

            {loading ? (

              <div className="text-center p-4">
                <Spinner animation="border" />
              </div>

            ) : (

              <Table responsive hover className="mb-0">

                <thead className="table-light">

                  <tr>
                    <th>Offer</th>
                    <th>End Date</th>
                    <th>Status</th>
                    <th>Home Page</th>
                    <th>Actions</th>
                  </tr>

                </thead>

                <tbody>

                  {filteredOffers.map((offer) => (

                    <tr key={offer.id}>

                      <td>

                        <div className="d-flex align-items-center">

                          <img
                            src={offer.listing_image}
                            alt={offer.title}
                            style={{
                              width: "60px",
                              height: "40px",
                              objectFit: "cover",
                              borderRadius: "4px",
                              marginRight: "12px"
                            }}
                          />

                          <div>

                            <div className="fw-medium">
                              {offer.title}
                            </div>

                          </div>

                        </div>

                      </td>

                      <td>
                        {formatDate(offer.end_date)}
                      </td>

                      <td>
                        {getStatusBadge(offer.status)}
                      </td>

                      <td>
                        {getHomeBadge(offer.show_in_home)}
                      </td>

                      <td>

                        <Dropdown>

                          <Dropdown.Toggle
                            variant="outline-secondary"
                            size="sm"
                          >
                            <i className="bi-three-dots"></i>
                          </Dropdown.Toggle>

                          <Dropdown.Menu>
                          {canWriteOffers && (
  <>
    <Dropdown.Item
      onClick={() => handleEditOffer(offer)}
    >
      <i className="bi-pencil me-2"></i>
      Edit
    </Dropdown.Item>

    <Dropdown.Divider />

    <Dropdown.Item
      className="text-danger"
      onClick={() => handleDeleteOffer(offer)}
    >
      <i className="bi-trash me-2"></i>
      Delete
    </Dropdown.Item>
  </>
)}

                          </Dropdown.Menu>

                        </Dropdown>

                      </td>

                    </tr>

                  ))}

                </tbody>

              </Table>

            )}

          </Card.Body>

        </Card>

      </div>

      <OfferModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onSave={handleSaveOffer}
        offer={selectedOffer}
        mode={modalMode}
      />

    </>
  );
};

export default Offers;